// Date/time, price, and distance formatting. Kept tiny and dependency free.

const dateFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

const timeFmt = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dateFmt.format(d);
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return timeFmt.format(d);
}

// "Sat, Jul 4 at 8:00 PM" style line for cards and popups.
export function formatDateTime(iso: string): string {
  const date = formatDate(iso);
  const time = formatTime(iso);
  if (!date) return '';
  return time ? `${date} at ${time}` : date;
}

export function formatPrice(min?: number, max?: number): string {
  if (typeof min !== 'number' && typeof max !== 'number') return '';
  if (typeof min === 'number' && typeof max === 'number') {
    if (min === max) return `$${round(min)}`;
    return `$${round(min)} to $${round(max)}`;
  }
  const v = (min ?? max) as number;
  return `from $${round(v)}`;
}

function round(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Great-circle distance in miles between two lat/lng points.
export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3958.8; // earth radius, miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// "0.4 mi" / "3 mi" / "12 mi away" friendly distance label.
export function formatDistance(miles: number): string {
  if (!Number.isFinite(miles)) return '';
  if (miles < 0.1) return 'Less than 0.1 mi away';
  if (miles < 10) return `${miles.toFixed(1)} mi away`;
  return `${Math.round(miles)} mi away`;
}

// YYYY-MM-DD key in local time, used to match events to calendar cells.
export function localDayKey(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
